<script lang="ts">
export enum ModalMode {
  Insert = 'insert',
  Edit = 'edit',
  Closed = 'closed',
}
</script>
<script setup lang="ts">
import { ref, nextTick, toRefs } from 'vue';
import { AkModal, AkInput, AkHeading4, AkParagraph, AkButton } from '@ankorstore/design-system';
import { useDomainValidation } from '@bc/shared/composables/use-domain-validation/use-domain-validation';

defineOptions({ name: 'LinkEditorModal' });

const props = defineProps<{
  copy: {
    title: string;
    description: string;
    labelPlaceholder: string;
    labelLabel: string;
    urlPlaceholder: string;
    urlLabel: string;
    cancel: string;
    submit: string;
    submitUpdate: string;
    errorRequired: string;
    errorWrongPrefix: string;
  }
}>();

const { copy } = toRefs(props);
const modal = ref<typeof AkModal>(null);
const { validateDomain } = useDomainValidation();

const labelInput = ref<typeof AkInput>(null);
const modalMode = ref<ModalMode>(ModalMode.Closed);

const label = ref('');
const url = ref('');
const errors = ref<{
  label: string;
  url: string;
}>({
  label: '',
  url: ''
});

const emit = defineEmits(['submit', 'cancel']);

const validateForm = () => {
  let isValid = true;

  if (!label.value) {
    errors.value.label = copy.value?.errorRequired;
    isValid = false;
  }

  const urlValidation = validateDomain(url.value);
  if (!urlValidation.isValid) {
    errors.value.url = urlValidation.errorMessage;
    isValid = false;
  }

  return isValid;
};

const clearForm = () => {
  label.value = '';
  url.value = '';

  clearErrors('label');
  clearErrors('url');
};

const handleSubmit = () => {
  if (!validateForm()) {
    return;
  }

  emit('submit', { label: label.value, url: url.value });
};

const open = async (options: { mode?: ModalMode, label?: string, url?: string }) => {
  modalMode.value = options.mode ?? ModalMode.Insert;
  modal.value.openModal();
  await nextTick();
  labelInput.value?.focusInput();

  if (options.label) {
    label.value = options.label;
  }

  if (options.url) {
    url.value = options.url;
  }
};

const close = () => {
  modalMode.value = ModalMode.Closed;
  modal.value.close();
  clearForm();
};

defineExpose({
  open,
  close,
});

const clearErrors = (field: 'label' | 'url') => {
  errors.value[field] = '';
};

</script>
<template>
  <Teleport to="body">
    <AkModal
      ref="modal"
      data-testid="insert-link-modal"
      @cancelled="emit('cancel')"
    >
      <div class="link-editor-modal__content">
        <AkHeading4 class="link-editor-modal__title">
          {{ copy.title }}
        </AkHeading4>
        <AkParagraph class="link-editor-modal__description">
          {{ copy.description }}
        </AkParagraph>
        <form
          data-testid="form"
          @submit.prevent="handleSubmit"
        >
          <AkInput
            ref="labelInput"
            v-model="label"
            :placeholder="copy.labelPlaceholder"
            :label="copy.labelLabel"
            class="link-editor-modal__input"
            :errors="errors.label"
            data-testid="label-input"
            @input="clearErrors('label')"
          />
          <AkInput
            v-model="url"
            :placeholder="copy.urlPlaceholder"
            :label="copy.urlLabel"
            class="link-editor-modal__input"
            :errors="errors.url"
            data-testid="url-input"
            @input="clearErrors('url')"
          />
          <div class="link-editor-modal__actions">
            <AkButton
              outlined
              data-testid="cancel-button"
              @click="emit('cancel')"
            >
              {{ copy.cancel }}
            </AkButton>
            <AkButton
              type="submit"
              data-testid="submit-button"
              @click.prevent="handleSubmit"
            >
              {{ modalMode === ModalMode.Edit ? copy.submitUpdate : copy.submit }}
            </AkButton>
          </div>
        </form>
      </div>
    </AkModal>
  </Teleport>
</template>

<style scoped lang="scss">
.link-editor-modal__content {
  @apply ds-text-left;

  .link-editor-modal__title {
    @apply ds-mb-4 ds-text-center;
  }

  .link-editor-modal__description {
    @apply ds-mb-4 ds-text-center;
  }

  .link-editor-modal__input {
    @apply ds-mb-4;
  }

  .link-editor-modal__actions {
    @apply ds-flex ds-justify-center ds-gap-4 ds-mt-1;
  }
}
</style>
